import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  SvgGradientStop,
  SvgRecolorOptions,
  SvgRecolorService
} from '../services/media/svg-recolor.service';

/**
 * Loads an SVG from `[appSvgRecolor]` (asset URL), recolors it with the
 * given solid color or gradient, and *replaces* the host element with the
 * resulting `<svg>` DOM node.
 *
 * The host element is treated purely as a placeholder; its `class` and
 * non-Angular attributes are copied onto the inserted `<svg>` so styling
 * keeps working. This avoids adding an extra wrapper element.
 *
 * Gradient stops accept either raw colors (e.g. `#ff0000`) or CSS custom
 * property names (e.g. `--base-gradient-color-1`); custom properties are
 * resolved against the host's computed style at apply time.
 */
@Directive({
  selector: '[appSvgRecolor]',
  standalone: true
})
export class SvgRecolorDirective implements OnChanges, OnDestroy {

  @Input('appSvgRecolor') public src!: string;
  @Input() public recolorColor?: string;
  @Input() public recolorGradient?: SvgGradientStop[];

  private subscription: Subscription | null = null;
  private currentSvg: SVGElement | null = null;
  private readonly placeholder: Element;
  private parent: Node | null = null;
  private hostDetached = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly svgRecolor: SvgRecolorService
  ) {
    this.placeholder = this.host.nativeElement;
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.render();
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.removeCurrentSvg();
  }

  private render(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;

    if (!this.parent && this.placeholder.parentNode) {
      this.parent = this.placeholder.parentNode;
    }

    if (!this.src) {
      this.removeCurrentSvg();
      return;
    }

    const options = this.buildOptions();
    if (!options) {
      return;
    }

    this.subscription = this.svgRecolor.recolor(this.src, options).subscribe({
      next: svgMarkup => this.swapInSvg(svgMarkup),
      error: () => this.removeCurrentSvg()
    });
  }

  private swapInSvg(svgMarkup: string): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const svgEl = doc.documentElement;

    if (!svgEl || svgEl.nodeName.toLowerCase() !== 'svg') {
      return;
    }

    // Bring the parsed SVG into the live document so it actually renders.
    const importedSvg = document.importNode(svgEl, true) as unknown as SVGElement;

    this.copyPlaceholderAttributes(importedSvg);

    const parent = this.parent ?? this.placeholder.parentNode;
    if (!parent) {
      return;
    }

    if (this.currentSvg && this.currentSvg.parentNode === parent) {
      parent.replaceChild(importedSvg, this.currentSvg);
    } else if (!this.hostDetached && this.placeholder.parentNode === parent) {
      parent.replaceChild(importedSvg, this.placeholder);
      this.hostDetached = true;
    } else {
      parent.appendChild(importedSvg);
    }

    this.currentSvg = importedSvg;
  }

  private copyPlaceholderAttributes(target: SVGElement): void {
    const attrs = this.placeholder.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i];
      const name = attr.name;
      // Skip the directive's own bindings; meaningless on the svg.
      if (name === 'appsvgrecolor') {
        continue;
      }
      if (name === 'class') {
        const existing = target.getAttribute('class');
        target.setAttribute(
          'class',
          existing ? `${existing} ${attr.value}` : attr.value
        );
      } else if (!target.hasAttribute(name)) {
        target.setAttribute(name, attr.value);
      }
    }
  }

  private removeCurrentSvg(): void {
    if (this.currentSvg && this.currentSvg.parentNode) {
      this.currentSvg.parentNode.removeChild(this.currentSvg);
    }
    this.currentSvg = null;
  }

  private buildOptions(): SvgRecolorOptions | null {
    if (this.recolorGradient && this.recolorGradient.length >= 2) {
      const resolved = this.recolorGradient.map(stop => ({
        offset: stop.offset,
        color: this.resolveColor(stop.color)
      }));
      return { gradientStops: resolved };
    }

    if (this.recolorColor) {
      return { color: this.resolveColor(this.recolorColor) };
    }

    return null;
  }

  private resolveColor(value: string): string {
    const trimmed = value.trim();
    if (trimmed.startsWith('--')) {
      const probe = this.placeholder.isConnected
        ? this.placeholder
        : (this.parent as Element | null) ?? document.documentElement;
      const computed = getComputedStyle(probe)
        .getPropertyValue(trimmed)
        .trim();
      return computed || trimmed;
    }
    return trimmed;
  }
}
